import { IconName } from '@fortawesome/fontawesome-common-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { uniqueId } from 'lodash'
import React, { FC, useState } from 'react'
import { replaceAndStrip } from '../../../utils/string'
import SuggestionChips from './SuggestionChips'

type TargetItem = {
  value: string
  label: string
  color: string
  icon: IconName
}

type Suggestion = {
  values: string[]
  legendBackground?: string
}

type UrlConditionProps = {
  targetItem: TargetItem
  words: string[]
  pattern: string
  onWordsChange: (words: string[]) => void
  onPatternChange: (pattern: string) => void
  suggestion?: Suggestion
}

const UrlCondition: FC<UrlConditionProps> = (props) => {
  const {
    targetItem,
    words,
    pattern,
    onWordsChange,
    onPatternChange,
    suggestion,
  } = props

  const [urlText, setUrlText] = useState<string>("")

  const addUrl = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing || e.key !== "Enter") return

    const trimValue = replaceAndStrip(urlText)
    if (!trimValue || words.includes(trimValue)) return

    onWordsChange([...words, trimValue])
    setUrlText("")
  }

  const blurUrl = (url: string) => {
    setUrlText("")
    const trimValue = replaceAndStrip(url)
    if (!trimValue || words.includes(trimValue)) return
    onWordsChange([...words, trimValue])
  }

  return (
    <>
      <div className="condition_header">
        <div className="condition_label">
          <FontAwesomeIcon
            icon={['fas', targetItem.icon as IconName]}
            style={{ color: targetItem.color }}
          />
          {targetItem.label}
        </div>
        <div className="match_select">
          <select
            key={`pattern_${pattern}`}
            onChange={(e) => onPatternChange(e.currentTarget.value)}
            defaultValue={pattern || "partial"}
          >
            <option value="partial">
              部分一致
            </option>
            <option value="exact">
              完全一致
            </option>
          </select>
          <div className="caret">
            <FontAwesomeIcon icon={['fas', 'caret-down']} />
          </div>
        </div>
      </div>
      <div>
        <div className="detail_box">
          {
            words.map((word) => (
              <div className="chip" key={uniqueId()}>
                {word}
                <span className="chip_remove" onClick={() => onWordsChange(words.filter((w) => w !== word))} role="none">
                  <FontAwesomeIcon icon={['fas', 'times']} />
                </span>
              </div>
            ))
          }
          <input
            className="keyword_input"
            type="text"
            value={urlText}
            onChange={(e) => setUrlText(e.target.value)}
            onKeyDown={(e) => addUrl(e)}
            onBlur={(e) => blurUrl(e.target.value)}
            placeholder={words.length ? "" : "URLを入力してエンターキーを押してください"}
          />
        </div>
      </div>
      <div>
        <div className="note_list">
          <div className="note">
            {`※ 指定URL${words.length > 1 ? "のいずれか" : ""}${pattern === "exact" ? "と一致している" : "が含まれている"}場合、自動仕分けされます。`}
          </div>
          <div className="note">
            ※ スペースや記号についても、含んだ状態のまま設定されます。ご注意ください。
          </div>
          <div className="note attention">
            ※ アプリケーションの自動仕分け条件設定が必須になります。併せてご設定ください。
          </div>
        </div>
      </div>
      {
        !!suggestion?.values.length && (
          <SuggestionChips
            values={suggestion.values}
            selectedValues={words}
            onToggle={(v) => onWordsChange(
              words.includes(v) ? words.filter((w) => w !== v) : [...words, v],
            )}
            legendBackground={suggestion.legendBackground}
          />
        )
      }
    </>
  )
}

export default UrlCondition
